'use client'

import React from 'react'
import Image from 'next/image'
import { Share } from 'lucide-react'
import { useSelector } from 'react-redux'
import { RootState } from 'features/store'
import { toast } from 'react-toastify'
import { buildShareUrl } from '@/lib/sharing'
import RatingStar from '@/components/RatingStar'
import SignUpCTA from './SignUpCTA'
import DOMPurify from 'isomorphic-dompurify'
import { PageContainer } from '@/components/ui/page-container'
import { PageBackButton } from '@/components/ui/page-back-button'
import FractionDisplay from '@/components/FractionDisplay'

const PREVIEW_STEP_COUNT = 2

interface RecipePreviewProps {
  recipe: {
    recipe_id: number
    recipe_name: string
    resourcePath?: string
    recipe_img_url?: string
    recipe_image?: {
      resource_path?: string
      thumbnail_path?: string
    }
    average_recipe_rating?: number
    total_time?: number
    cook_time?: number
    preparation_time?: number
    number_of_servings?: number
    recipe_ingredients?: any[]
    recipe_steps?: any[]
    user?: {
      full_name?: string
      profile_photo_resource?: string
      slug?: string
    }
  }
}

const RecipePreview: React.FC<RecipePreviewProps> = ({ recipe }) => {
  const user = useSelector((state: RootState) => state.culineerAuth.user)

  const getRecipeImage = () => {
    // Priority order: resourcePath > recipe_img_url > recipe_image paths
    return recipe?.resourcePath || recipe?.recipe_img_url || null
  }

  // Logged-out visitors and crawlers get the full ingredient list and the
  // first couple of steps — enough to get the page indexed and useful, while
  // the full multi-step cooking view, save, and print stay behind login.
  const sortedIngredients = [...(recipe?.recipe_ingredients || [])]
    .map((ri: any) => ({
      record_id: ri?.record_id,
      sort_order: ri?.sort_order,
      ingredient_id: ri?.ingredient_id,
      whole_number: ri?.whole_number,
      numerator: ri?.numerator,
      denominator: ri?.denominator,
      unit_abbreviation: ri?.unit_of_measurement?.unit_abbreviation,
      name: ri?.ingredient?.ingredient_name || ri?.raw_name,
    }))
    .sort((a: any, b: any) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0))

  const sortedSteps = [...(recipe?.recipe_steps || [])].sort(
    (a: any, b: any) => (a?.step_order ?? 0) - (b?.step_order ?? 0)
  )
  const previewSteps = sortedSteps.slice(0, PREVIEW_STEP_COUNT)
  const hasMoreSteps = sortedSteps.length > previewSteps.length

  return (
    <div className="w-full h-full overflow-y-auto">
      <PageContainer className="py-10">
        <div className="flex items-center justify-between gap-3 mb-4">
          <PageBackButton fallbackHref="/recipes" />
          <button
              onClick={() => {
                const contentId = String(recipe?.recipe_id)
                const shareUrl = buildShareUrl(window.location.pathname, 'recipe', contentId, user?.slug)
                navigator.clipboard.writeText(shareUrl)
                toast.success('Link copied to clipboard!')
              }}
              className="flex items-center px-3 py-2 space-x-2 border border-primary rounded-xl"
            >
              <Share size={20} color="#85C349" />
              <span className="text-sm">Share</span>
            </button>
        </div>
        <h1 className="text-2xl font-semibold mb-4">{recipe?.recipe_name}</h1>

        {/* Rating */}
        <div className="flex items-center py-2 space-x-2 mb-4">
          <RatingStar rating={Number(recipe?.average_recipe_rating) || 0} />
        </div>

        {/* Recipe Image */}
        <div className="relative mb-6 mx-auto aspect-video w-full max-w-[1200px] max-h-[394px] overflow-hidden rounded-2xl bg-[#F7F7F7]">
          {getRecipeImage() ? (
            <Image
              src={getRecipeImage() as string}
              alt={recipe?.recipe_name || 'Recipe Image'}
              fill
              sizes="(max-width: 1440px) calc(100vw - 64px), 1200px"
              className="object-cover"
              unoptimized
              onError={(e) => {
                const target = e.target as HTMLImageElement
                target.src = '/assets/icons/Recipe.svg'
              }}
            />
          ) : (
            <img
              className="mx-auto h-48 w-auto"
              src="/assets/icons/Recipe.svg"
              alt="Recipe placeholder"
            />
          )}
        </div>

        {/* Recipe Stats */}
        <div className="flex w-full items-center justify-between sm:flex-row xs:flex-col mb-6">
          <div className="flex w-full min-w-max gap-4 divide-x divide-blue-gray-100 py-6 pb-4 pt-4 sm:flex-row xs:flex-col">
            <div className="flex sm:flex-col sm:pr-0 xs:flex-row-reverse xs:items-center xs:justify-between xs:pr-0">
              <h2 className="text-center text-2xl text-[#0D1000]">
                {Math.floor(Number(recipe?.total_time) / 60) > 0 &&
                  Math.floor(Number(recipe?.total_time) / 60)}{' '}
                <small className="text-[12px]">
                  {Math.floor(Number(recipe?.total_time) / 60) > 0 && 'hr(s)'}
                </small>{' '}
                {Number(recipe?.total_time) % 60 < 1
                  ? 0
                  : Number(recipe?.total_time) % 60}{' '}
                <small className="text-[12px]">{'mins'}</small>
              </h2>
              <small className="text-center text-grayText">Total time</small>
            </div>
            <div className="flex sm:flex-col sm:pl-8 sm:pr-8 xs:flex-row-reverse xs:items-center xs:justify-between xs:pl-0 xs:pr-0">
              <h2 className="text-center text-2xl text-[#0D1000]">
                {recipe?.number_of_servings || 0}
              </h2>
              <small className="text-center text-grayText">Serving</small>
            </div>
            <div className="flex space-y-1 sm:flex-col sm:pl-8 sm:pr-8 xs:flex-row-reverse xs:items-center xs:justify-between xs:pl-0 xs:pr-0">
              <h2 className="text-center text-2xl text-[#0D1000]">
                {recipe?.recipe_ingredients?.length || 0}
              </h2>
              <small className="text-center text-grayText">Ingredients</small>
            </div>
          </div>
        </div>

        {/* Cook Mode Toggle (Visual Only) */}
        <div className="mb-6 flex h-16 min-w-max items-center justify-center rounded-xl border border-gray-200 bg-gray-50 px-6 shadow-inner">
          <div className="flex items-center space-x-5">
            <div className="flex items-center space-x-2">
              <span className="w-full text-[15px] text-blue-gray-400">
                Enable cook mode
              </span>
            </div>
            <div className="h-6 w-12 rounded-full bg-gray-300"></div>
          </div>
        </div>

        {/* Full ingredient list — visible to logged-out visitors and crawlers */}
        {sortedIngredients.length > 0 && (
          <div className="mb-6">
            <h3 className="mb-3 text-lg font-semibold">Ingredients</h3>
            <div className="flex flex-col gap-3">
              {sortedIngredients.map((item, index: number) => (
                <div
                  key={item.record_id ?? `${item.ingredient_id}-${index}`}
                  className="flex items-start gap-3 text-sm leading-[17px] text-[#0D1000]"
                >
                  <span className="min-w-[28px] shrink-0 font-semibold">
                    <FractionDisplay
                      whole_nmuber={Number(item?.whole_number)}
                      numerator={Number(item?.numerator)}
                      denominator={Number(item?.denominator)}
                    />
                  </span>
                  <span className="w-[60px] shrink-0">{item?.unit_abbreviation ?? ''}</span>
                  <span
                    className="min-w-0 flex-1"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item?.name ?? '') }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* First couple of steps — full multi-step cooking view stays behind login */}
        {previewSteps.length > 0 && (
          <div className="mb-6">
            <h3 className="mb-3 text-lg font-semibold">Directions</h3>
            <div className="flex flex-col gap-5">
              {previewSteps.map((step: any, index: number) => (
                <div key={step?.record_id ?? index} className="flex flex-col gap-2">
                  <h4 className="text-base font-bold leading-[22px] text-[#202020]">
                    Step {index + 1}
                  </h4>
                  <div
                    className="text-base font-normal leading-6 text-[#333333]"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(step?.step_name ?? ''),
                    }}
                  />
                </div>
              ))}
            </div>
            {hasMoreSteps && (
              <div className="relative mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 text-center text-sm text-gray-500">
                <div className="absolute inset-x-0 -top-4 h-4 bg-gradient-to-b from-transparent to-gray-50" />
                {sortedSteps.length - previewSteps.length} more step
                {sortedSteps.length - previewSteps.length === 1 ? '' : 's'} — sign up to see the rest
              </div>
            )}
          </div>
        )}

        {/* Sign Up CTA */}
        <SignUpCTA message="To view this recipe and access thousands of others, sign up for free or, log in" />
      </PageContainer>
    </div>
  )
}

export default RecipePreview

